"use client";
import { useI18n } from "@wojo/localization";
import {
    Avatar,
    Card,
    Container,
    MenuItem,
    Modal,
    Text,
    transformAsset,
} from "@wojo/ui";

import { useEffect, useState } from "react";
import { ZendeskHelpCenterLink } from "@wojo/web-components";
import { FiExternalLink, FiList } from "react-icons/fi";
import styles from "./AccountNavigation.module.scss";
import { NavItem } from "./NavItem";
import { useNavItems } from "./useNavItems";
import { useNavProfile } from "@myaccount/_client/hooks";
import { gtmSendEvent } from "@wojo/analytics";
import Link from "next/link";
import { usePathname } from "next/navigation";

export const AccountNavigation: React.FC = () => {
    const { accountDisplayText, accountInitials, accountAvatar } =
        useNavProfile();

    const l10n = useI18n("account").account.navigation;
    const { global } = useI18n("global");

    const navItems = useNavItems();
    const [isOpenModal, setIsOpenModal] = useState(false);

    const pathname = usePathname();

    useEffect(() => {
        // Close the modal when the route changes, usually by next/link <Link> component
        setIsOpenModal(false);
    }, [pathname]);

    const inner = (
        <>
            <div className={styles["account-header"]}>
                <Link
                    href={l10n.profile.url.l()}
                    className={styles["account-header-link"]}
                >
                    <Avatar
                        initials={accountInitials}
                        src={transformAsset(accountAvatar, {
                            size: {
                                width: 60,
                                height: 60,
                            },
                        })}
                        size={60}
                    />

                    <Text.Heading order="6" tag="div" className="account-name">
                        {accountDisplayText}
                    </Text.Heading>
                </Link>
            </div>
            <nav
                className={styles["account-links"]}
                aria-label={l10n.navigationLabel.l()}
            >
                <ul
                    className={styles["account-links-list"]}
                    aria-label={l10n.navigationLabel.l()}
                >
                    {navItems.map((prop) => (
                        <NavItem key={prop.url} {...prop} />
                    ))}
                    {process.env.NEXT_PUBLIC_ZENDESK_HELP_CENTER_URL && (
                        <MenuItem tag="span">
                            <ZendeskHelpCenterLink
                                className={styles["zendesk-link"]}
                                adornLeft={
                                    <FiExternalLink
                                        aria-hidden="true"
                                        size={20}
                                    />
                                }
                                onClick={() => {
                                    gtmSendEvent({
                                        event: "track",
                                        ga4Tracking: {
                                            ga4_object: "MY_ACCOUNT_NAVIGATION",
                                            ga4_action: "SELECTED",
                                            section: "Help Center",
                                            navigationSelection: "Help Center",
                                        },
                                    });
                                }}
                            >
                                {global.accountNavigation.helpCenter.text.l()}
                            </ZendeskHelpCenterLink>
                        </MenuItem>
                    )}
                </ul>
            </nav>
        </>
    );
    return (
        <div className={styles["account-nav-container"]}>
            <Card
                tag="button"
                type="button"
                className={styles["account-nav-menu-button"]}
                onClick={() => {
                    setIsOpenModal(true);
                }}
            >
                <FiList
                    aria-hidden={true}
                    style={{ height: "20px", width: "20px" }}
                />
                <span>{l10n.myAccountMenu.l()}</span>
            </Card>
            <Modal
                isOpen={isOpenModal}
                accessibleTitle={l10n.modalTitle.l()}
                variant="fullscreen"
                onCloseRequested={() => {
                    setIsOpenModal(false);
                }}
            >
                <Container contentsClassName={styles["account-nav-container"]}>
                    {inner}
                </Container>
            </Modal>

            <Card variant="small" className={styles["account-nav"]}>
                {inner}
            </Card>
        </div>
    );
};
